import { Badge, Button, EmptyState, formatPrice, type Tone } from "@coucou/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { downloadFromApi, formatDay, parseEuros } from "../lib/download";
import { SALON_ID, api } from "../lib/trpc";

type Tab = "sales" | "products";
type State = "active" | "used" | "expired" | "pending_payment" | "cancelled";

const STATE_TONES: Record<State, Tone> = {
  active: "success",
  used: "muted",
  expired: "warning",
  pending_payment: "neutral",
  cancelled: "danger",
};

const FILTERS: { key: State | null; label: string }[] = [
  { key: null, label: "Toutes" },
  { key: "active", label: "Valables" },
  { key: "used", label: "Utilisées" },
  { key: "expired", label: "Expirées" },
  { key: "cancelled", label: "Annulées" },
  // Les paniers abandonnés existent, mais on ne les montre que sur demande.
  { key: "pending_payment", label: "Paiement en attente" },
];

const METHOD_LABELS: Record<string, string> = {
  card_online: "En ligne",
  card_terminal: "Carte au salon",
  cash: "Espèces",
  transfer: "Virement",
  gift_card: "Carte cadeau",
};

const TX_LABELS: Record<string, string> = {
  issue: "Émission",
  redeem: "Utilisation",
  recredit: "Recrédit",
  adjust: "Correction",
  cancel: "Annulation",
};

/**
 * Cartes cadeaux.
 *
 * Deux usages, deux onglets : le quotidien (retrouver une carte, en vendre une
 * au comptoir) et le paramétrage (les types de cartes, une fois par saison).
 * L'utilisation d'une carte, elle, ne se fait PAS ici : elle se fait à
 * l'encaissement du rendez-vous, là où l'on est déjà.
 */
export function GiftCards({ isManager }: { isManager: boolean }) {
  const [tab, setTab] = useState<Tab>("sales");
  const [selling, setSelling] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="shrink-0 px-6 pb-2 pt-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl">Cartes cadeaux</h1>
          <Button onClick={() => setSelling(true)}>🎁 Vendre une carte</Button>
        </div>
        <div className="flex flex-wrap gap-1">
          {(
            [
              { key: "sales", label: "Ventes" },
              { key: "products", label: "Types de cartes" },
            ] as const
          ).map((entry) => (
            <button
              key={entry.key}
              type="button"
              onClick={() => setTab(entry.key)}
              aria-current={tab === entry.key ? "page" : undefined}
              className={[
                "min-h-9 rounded-full px-4 text-sm transition-colors",
                tab === entry.key ? "bg-blush-500 font-medium text-white" : "text-encre-600 hover:bg-creme-200",
              ].join(" ")}
            >
              {entry.label}
            </button>
          ))}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-auto px-6 pb-8 pt-4">
        {tab === "sales" && <SalesPanel onOpen={setOpenId} />}
        {tab === "products" && <ProductsPanel isManager={isManager} />}
      </div>

      {selling && (
        <SellDialog
          onClose={() => setSelling(false)}
          onSold={(id) => {
            setSelling(false);
            setTab("sales");
            setOpenId(id);
          }}
        />
      )}
      {openId && <CardDrawer id={openId} isManager={isManager} onClose={() => setOpenId(null)} />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Ventes                                                                     */
/* -------------------------------------------------------------------------- */

function SalesPanel({ onOpen }: { onOpen: (id: string) => void }) {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [state, setState] = useState<State | null>(null);
  const [page, setPage] = useState(1);

  // La recherche part 250 ms après la dernière frappe : un code se tape vite.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => setPage(1), [debounced, state]);

  const stats = useQuery({
    queryKey: ["gift-cards", "stats"],
    queryFn: () => api.giftCards.stats.query({ salonId: SALON_ID }),
  });

  const list = useQuery({
    queryKey: ["gift-cards", "list", debounced, state, page],
    queryFn: () =>
      api.giftCards.list.query({
        salonId: SALON_ID,
        search: debounced || undefined,
        state: state ?? undefined,
        page,
      }),
    placeholderData: keepPreviousData,
  });

  const s = stats.data;
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / list.data.pageSize)) : 1;

  return (
    <>
      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Vendues"
          value={s ? formatPrice(s.soldCents) : "…"}
          hint={s ? `${s.soldCount} carte${s.soldCount > 1 ? "s" : ""}` : undefined}
        />
        <Stat
          label="En circulation"
          value={s ? formatPrice(s.outstandingCents) : "…"}
          hint={s ? `${s.activeCount} carte${s.activeCount > 1 ? "s" : ""} valable${s.activeCount > 1 ? "s" : ""}` : undefined}
        />
        <Stat label="Utilisé en prestations" value={s ? formatPrice(s.redeemedCents) : "…"} />
        <Stat
          label="Expiré sans être utilisé"
          value={s ? formatPrice(s.expiredCents) : "…"}
          hint={
            s && s.expiringSoonCount > 0
              ? `${s.expiringSoonCount} expire${s.expiringSoonCount > 1 ? "nt" : ""} dans 30 jours`
              : undefined
          }
          tone={s && s.expiringSoonCount > 0 ? "warning" : undefined}
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Code, nom ou e-mail…"
          aria-label="Rechercher une carte"
          className="h-10 w-full max-w-xs rounded-lg border border-creme-300 bg-white px-3"
        />
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((filter) => (
            <button
              key={filter.label}
              type="button"
              onClick={() => setState(filter.key)}
              aria-pressed={state === filter.key}
              className={[
                "min-h-9 rounded-full px-3.5 text-sm transition-colors",
                state === filter.key ? "bg-encre-800 text-white" : "bg-creme-100 text-encre-700 hover:bg-creme-200",
              ].join(" ")}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {list.isPending && <p className="text-encre-400">Chargement…</p>}

      {list.data?.cards.length === 0 && (
        <EmptyState
          glyph="🎁"
          title={debounced || state ? "Aucune carte ne correspond" : "Aucune carte vendue pour l'instant"}
          description={
            debounced || state
              ? "Essayez un autre code, ou retirez le filtre."
              : "Les cartes achetées sur le site et vendues au salon apparaîtront ici."
          }
        />
      )}

      {list.data && list.data.cards.length > 0 && (
        <div className="overflow-hidden rounded-xl bg-white shadow-soft">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-creme-200 text-xs uppercase tracking-wide text-encre-400">
              <tr>
                <th className="px-4 py-3 font-medium">Code</th>
                <th className="px-4 py-3 font-medium">Pour · de la part de</th>
                <th className="px-4 py-3 text-right font-medium">Montant</th>
                <th className="px-4 py-3 text-right font-medium">Solde</th>
                <th className="px-4 py-3 font-medium">État</th>
                <th className="px-4 py-3 font-medium">Achetée</th>
              </tr>
            </thead>
            <tbody>
              {list.data.cards.map((card) => (
                <tr
                  key={card.id}
                  onClick={() => onOpen(card.id)}
                  className="cursor-pointer border-b border-creme-100 last:border-0 hover:bg-creme-50"
                >
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => onOpen(card.id)}
                      className="font-mono text-[13px] tracking-wide text-encre-900"
                    >
                      {card.code}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-encre-800">{card.recipientName ?? card.purchaserName}</p>
                    {card.recipientName && (
                      <p className="text-xs text-encre-400">de {card.purchaserName}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-encre-600">
                    {formatPrice(card.initialAmountCents)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums text-encre-900">
                    {formatPrice(card.balanceCents)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={STATE_TONES[card.state]} dot>
                      {card.stateLabel}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-encre-500">
                    {formatDay(card.paidAt ?? card.createdAt)}
                    <span className="block text-xs text-encre-400">
                      {card.channel === "online" ? "Sur le site" : "Au salon"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3 text-sm">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            ← Précédentes
          </Button>
          <span className="tabular-nums text-encre-500">
            Page {page} sur {pages}
          </span>
          <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>
            Suivantes →
          </Button>
        </div>
      )}
    </>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "warning";
}) {
  return (
    <div className="rounded-xl bg-white p-4 shadow-soft">
      <p className="text-xs uppercase tracking-wide text-encre-400">{label}</p>
      <p className="mt-1 font-display text-2xl text-encre-900" data-numeric>
        {value}
      </p>
      {hint && (
        <p className={["mt-0.5 text-sm", tone === "warning" ? "text-ambre-700" : "text-encre-500"].join(" ")}>
          {hint}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Fiche d'une carte                                                          */
/* -------------------------------------------------------------------------- */

function CardDrawer({ id, isManager, onClose }: { id: string; isManager: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [action, setAction] = useState<null | "adjust" | "extend" | "cancel">(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ["gift-cards", "detail", id],
    queryFn: () => api.giftCards.detail.query({ id }),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["gift-cards"] });
  };

  const resend = useMutation({
    mutationFn: (audience: "purchaser" | "recipient") => api.giftCards.resend.mutate({ id, audience }),
    onSuccess: (result) => {
      setNotice(`E-mail renvoyé à ${result.to}.`);
      refresh();
    },
    onError: (error) => setNotice(error instanceof Error ? error.message : "Envoi impossible."),
  });

  const card = detail.data;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Carte cadeau"
      className="fixed inset-0 z-50 flex justify-end bg-encre-900/30 animate-fade-in"
      onClick={onClose}
    >
      <aside
        className="h-full w-full max-w-md overflow-y-auto bg-white p-6 shadow-lift"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="text-2xl">Carte cadeau</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="rounded-full px-2 text-encre-400 hover:bg-creme-100"
          >
            ×
          </button>
        </div>

        {detail.isPending && <p className="text-encre-400">Chargement…</p>}

        {card && (
          <>
            {/* La carte, telle qu'elle a été offerte : on reconnaît ce qu'on cherche. */}
            <div
              className="rounded-xl p-5 text-white shadow-soft"
              style={{ backgroundColor: card.accentColorSnapshot }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-90">
                {card.productNameSnapshot}
              </p>
              <p className="mt-2 font-display text-4xl" data-numeric>
                {formatPrice(card.balanceCents)}
              </p>
              <p className="text-sm opacity-90">
                restant sur {formatPrice(card.initialAmountCents)}
              </p>
              <p className="mt-4 select-all font-mono text-lg tracking-wider">{card.code}</p>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Badge tone={STATE_TONES[card.state]} dot>
                {card.stateLabel}
              </Badge>
              <span className="text-sm text-encre-500">
                Valable jusqu'au {formatDay(card.expiresAt)}
              </span>
            </div>

            <dl className="mt-5 grid grid-cols-[8rem_1fr] gap-x-3 gap-y-2 text-sm">
              <dt className="text-encre-400">Offerte par</dt>
              <dd className="text-encre-800">
                {card.purchaserName}
                {card.purchaserEmail && <span className="block text-encre-500">{card.purchaserEmail}</span>}
              </dd>
              <dt className="text-encre-400">Pour</dt>
              <dd className="text-encre-800">
                {card.recipientName ?? "—"}
                {card.recipientEmail && <span className="block text-encre-500">{card.recipientEmail}</span>}
              </dd>
              <dt className="text-encre-400">Paiement</dt>
              <dd className="text-encre-800">
                {card.paymentMethod ? METHOD_LABELS[card.paymentMethod] : "En attente"}
                {card.paidAt && <span className="text-encre-500"> · {formatDay(card.paidAt)}</span>}
                {card.soldBy && <span className="block text-encre-500">Vendue par {card.soldBy.firstName}</span>}
              </dd>
              {card.deliverAt && (
                <>
                  <dt className="text-encre-400">Envoi prévu</dt>
                  <dd className="text-encre-800">{formatDay(card.deliverAt)}</dd>
                </>
              )}
              {card.cancellationReason && (
                <>
                  <dt className="text-encre-400">Annulée</dt>
                  <dd className="text-grenat-700">{card.cancellationReason}</dd>
                </>
              )}
            </dl>

            {card.message && (
              <p className="mt-4 rounded-md bg-creme-100 px-3 py-2 text-sm italic text-encre-600">
                « {card.message} »
              </p>
            )}

            {card.status === "active" && (
              <div className="mt-5 space-y-2">
                <Button
                  block
                  variant="secondary"
                  onClick={() => {
                    setDownloadError(null);
                    downloadFromApi(`/gift-cards/${card.id}/pdf`, `carte-cadeau-${card.code}.pdf`).catch((error) =>
                      setDownloadError(error instanceof Error ? error.message : "Téléchargement impossible."),
                    );
                  }}
                >
                  ⬇︎ Télécharger le PDF
                </Button>
                {downloadError && <p className="text-sm text-grenat-700">{downloadError}</p>}

                <EmailLine
                  label="E-mail à l'acheteuse"
                  email={card.purchaserEmail}
                  sentAt={card.purchaserEmailSentAt}
                  pending={resend.isPending && resend.variables === "purchaser"}
                  onResend={() => resend.mutate("purchaser")}
                />
                {card.recipientEmail && (
                  <EmailLine
                    label="E-mail à la bénéficiaire"
                    email={card.recipientEmail}
                    sentAt={card.recipientEmailSentAt}
                    scheduledFor={card.deliverAt}
                    pending={resend.isPending && resend.variables === "recipient"}
                    onResend={() => resend.mutate("recipient")}
                  />
                )}
                {notice && <p className="text-sm text-encre-600">{notice}</p>}
              </div>
            )}

            {/* Corrections : réservées à la gérante, et toujours motivées. */}
            {isManager && card.status === "active" && (
              <div className="mt-6 border-t border-creme-200 pt-4">
                <p className="mb-2 text-sm font-medium text-encre-700">Corrections</p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setAction("extend")}>
                    Prolonger
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setAction("adjust")}>
                    Corriger le solde
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setAction("cancel")}>
                    Annuler la carte
                  </Button>
                </div>
                {action && (
                  <CorrectionForm
                    kind={action}
                    card={card}
                    onDone={() => {
                      setAction(null);
                      refresh();
                    }}
                    onCancel={() => setAction(null)}
                  />
                )}
              </div>
            )}

            <h3 className="mb-2 mt-6 text-lg">Historique</h3>
            <ol className="space-y-2">
              {card.transactions.map((tx) => (
                <li key={tx.id} className="flex items-start justify-between gap-3 rounded-lg bg-creme-50 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <p className="text-encre-800">
                      {TX_LABELS[tx.kind] ?? tx.kind}
                      {tx.appointment && (
                        <span className="text-encre-500"> · rendez-vous n° {tx.appointment.reference}</span>
                      )}
                    </p>
                    <p className="text-xs text-encre-400">
                      {new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" }).format(
                        tx.createdAt,
                      )}
                      {tx.actor && ` · ${tx.actor.firstName}`}
                      {tx.note && ` · ${tx.note}`}
                    </p>
                  </div>
                  <div className="text-right tabular-nums">
                    <p className={tx.amountCents < 0 ? "text-encre-800" : "text-sauge-700"}>
                      {tx.amountCents > 0 ? "+" : "−"}
                      {formatPrice(Math.abs(tx.amountCents))}
                    </p>
                    <p className="text-xs text-encre-400">reste {formatPrice(tx.balanceAfterCents)}</p>
                  </div>
                </li>
              ))}
              {card.transactions.length === 0 && (
                <li className="text-sm text-encre-400">Aucun mouvement : la carte n'est pas encore payée.</li>
              )}
            </ol>
          </>
        )}
      </aside>
    </div>
  );
}

function EmailLine({
  label,
  email,
  sentAt,
  scheduledFor,
  pending,
  onResend,
}: {
  label: string;
  email: string | null;
  sentAt: Date | null;
  scheduledFor?: Date | null;
  pending: boolean;
  onResend: () => void;
}) {
  const differe = !sentAt && scheduledFor && scheduledFor.getTime() > Date.now();
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-creme-200 px-3 py-2 text-sm">
      <div>
        <p className="text-encre-700">{label}</p>
        <p className="text-xs text-encre-400">
          {!email
            ? "Aucune adresse"
            : sentAt
              ? `Envoyé le ${formatDay(sentAt)}`
              : differe
                ? `Programmé pour le ${formatDay(scheduledFor)}`
                : "Pas encore parti"}
        </p>
      </div>
      {email && (
        <Button size="sm" variant="ghost" loading={pending} onClick={onResend}>
          {sentAt ? "Renvoyer" : "Envoyer"}
        </Button>
      )}
    </div>
  );
}

function CorrectionForm({
  kind,
  card,
  onDone,
  onCancel,
}: {
  kind: "adjust" | "extend" | "cancel";
  card: { id: string; balanceCents: number; expiresAt: Date };
  onDone: () => void;
  onCancel: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [direction, setDirection] = useState<"credit" | "debit">("credit");
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(() => {
    const prochain = new Date(card.expiresAt);
    prochain.setMonth(prochain.getMonth() + 3);
    return prochain.toISOString().slice(0, 10);
  });

  const mutation = useMutation({
    mutationFn: async () => {
      if (kind === "extend") return api.giftCards.extend.mutate({ id: card.id, expiresOn: date });
      if (kind === "cancel") return api.giftCards.cancel.mutate({ id: card.id, reason });
      const cents = parseEuros(amount) ?? 0;
      return api.giftCards.adjust.mutate({
        id: card.id,
        amountCents: direction === "credit" ? cents : -cents,
        reason,
      });
    },
    onSuccess: onDone,
  });

  return (
    <form
      className="mt-3 space-y-3 rounded-lg bg-creme-50 p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (kind === "cancel" && !window.confirm("Annuler cette carte ? Son solde sera perdu.")) return;
        mutation.mutate();
      }}
    >
      {kind === "extend" && (
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-encre-700">Nouvelle date de fin</span>
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            required
            className="h-10 w-full rounded-lg border border-creme-300 bg-white px-3"
          />
        </label>
      )}

      {kind === "adjust" && (
        <div className="flex gap-2">
          <select
            value={direction}
            onChange={(event) => setDirection(event.target.value as "credit" | "debit")}
            aria-label="Sens de la correction"
            className="h-10 rounded-lg border border-creme-300 bg-white px-2 text-sm"
          >
            <option value="credit">Ajouter</option>
            <option value="debit">Retirer</option>
          </select>
          <input
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="Montant"
            aria-label="Montant en euros"
            required
            className="h-10 w-full rounded-lg border border-creme-300 bg-white px-3 tabular-nums"
          />
          <span className="self-center text-encre-500">€</span>
        </div>
      )}

      {kind !== "extend" && (
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-encre-700">Motif</span>
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            required
            minLength={3}
            placeholder={kind === "cancel" ? "Remboursée à la cliente…" : "Erreur d'encaissement du 12/10…"}
            className="h-10 w-full rounded-lg border border-creme-300 bg-white px-3"
          />
        </label>
      )}

      {mutation.isError && (
        <p role="alert" className="text-sm text-grenat-700">
          {mutation.error instanceof Error ? mutation.error.message : "Correction impossible."}
        </p>
      )}

      <div className="flex gap-2">
        <Button type="submit" size="sm" variant={kind === "cancel" ? "danger" : "primary"} loading={mutation.isPending}>
          {kind === "extend" ? "Prolonger" : kind === "cancel" ? "Annuler la carte" : "Corriger"}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Retour
        </Button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/*  Vente au comptoir                                                          */
/* -------------------------------------------------------------------------- */

function SellDialog({ onClose, onSold }: { onClose: () => void; onSold: (id: string) => void }) {
  const queryClient = useQueryClient();
  const products = useQuery({
    queryKey: ["gift-cards", "products"],
    queryFn: () => api.giftCards.products.query({ salonId: SALON_ID }),
  });
  const actifs = (products.data ?? []).filter((product) => product.isActive);

  const [productId, setProductId] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<"card_terminal" | "cash" | "transfer">("card_terminal");
  const [form, setForm] = useState({
    purchaserName: "",
    purchaserEmail: "",
    recipientName: "",
    recipientEmail: "",
    message: "",
  });
  const [sendEmails, setSendEmails] = useState(true);

  const product = actifs.find((entry) => entry.id === productId) ?? actifs[0];
  useEffect(() => {
    if (product && !amount && product.presetAmountsCents[0]) {
      setAmount(String(product.presetAmountsCents[0] / 100));
    }
  }, [product, amount]);

  const cents = parseEuros(amount);
  const sell = useMutation({
    mutationFn: () =>
      api.giftCards.sell.mutate({
        salonId: SALON_ID,
        productId: product!.id,
        amountCents: cents!,
        method,
        purchaserName: form.purchaserName.trim(),
        purchaserEmail: form.purchaserEmail.trim() || undefined,
        recipientName: form.recipientName.trim() || undefined,
        recipientEmail: form.recipientEmail.trim() || undefined,
        message: form.message.trim() || undefined,
        sendEmails,
      }),
    onSuccess: (card) => {
      queryClient.invalidateQueries({ queryKey: ["gift-cards"] });
      onSold(card.id);
    },
  });

  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const hasEmail = Boolean(form.purchaserEmail.trim() || form.recipientEmail.trim());

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Vendre une carte cadeau"
      className="fixed inset-0 z-50 flex items-center justify-center bg-encre-900/30 p-6 animate-fade-in"
      onClick={onClose}
    >
      <form
        className="max-h-full w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-lift animate-fade-up"
        onClick={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          sell.mutate();
        }}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl">Vendre une carte</h2>
            <p className="text-sm text-encre-500">Payée au salon : elle est valable tout de suite.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="rounded-full px-2 text-encre-400 hover:bg-creme-100"
          >
            ×
          </button>
        </div>

        {products.data && actifs.length === 0 && (
          <p className="mb-4 rounded-md bg-ambre-100 px-3 py-2 text-sm text-ambre-700">
            Aucun type de carte actif. Créez-en un dans l'onglet « Types de cartes ».
          </p>
        )}

        {actifs.length > 1 && (
          <label className="mb-4 block text-sm">
            <span className="mb-1 block font-medium text-encre-700">Type de carte</span>
            <select
              value={product?.id ?? ""}
              onChange={(event) => {
                setProductId(event.target.value);
                setAmount("");
              }}
              className="h-10 w-full rounded-lg border border-creme-300 px-3"
            >
              {actifs.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {product && (
          <fieldset className="mb-4">
            <legend className="mb-2 text-sm font-medium text-encre-700">Montant</legend>
            <div className="flex flex-wrap items-center gap-2">
              {product.presetAmountsCents.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setAmount(String(preset / 100))}
                  aria-pressed={cents === preset}
                  className={[
                    "min-h-10 rounded-full px-4 text-sm tabular-nums transition-colors",
                    cents === preset ? "bg-blush-500 text-white" : "bg-creme-100 text-encre-700 hover:bg-creme-200",
                  ].join(" ")}
                >
                  {formatPrice(preset)}
                </button>
              ))}
              <div className="flex items-center gap-1.5">
                <input
                  inputMode="decimal"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  aria-label="Montant libre en euros"
                  className="h-10 w-24 rounded-lg border border-creme-300 px-3 tabular-nums"
                />
                <span className="text-encre-500">€</span>
              </div>
            </div>
          </fieldset>
        )}

        <fieldset className="mb-4">
          <legend className="mb-2 text-sm font-medium text-encre-700">Réglée par</legend>
          <div className="grid grid-cols-3 gap-2">
            {(
              [
                { key: "card_terminal", label: "💳 Carte" },
                { key: "cash", label: "💶 Espèces" },
                { key: "transfer", label: "🏦 Virement" },
              ] as const
            ).map((entry) => (
              <button
                key={entry.key}
                type="button"
                onClick={() => setMethod(entry.key)}
                aria-pressed={method === entry.key}
                className={[
                  "min-h-11 rounded-lg px-3 text-sm transition-colors",
                  method === entry.key ? "bg-blush-500 text-white" : "bg-creme-100 text-encre-700 hover:bg-creme-200",
                ].join(" ")}
              >
                {entry.label}
              </button>
            ))}
          </div>
        </fieldset>

        <div className="grid gap-3 sm:grid-cols-2">
          <TextField label="Offerte par" value={form.purchaserName} onChange={set("purchaserName")} required />
          <TextField label="Son e-mail (facultatif)" type="email" value={form.purchaserEmail} onChange={set("purchaserEmail")} />
          <TextField label="Pour (facultatif)" value={form.recipientName} onChange={set("recipientName")} />
          <TextField label="E-mail de la bénéficiaire (facultatif)" type="email" value={form.recipientEmail} onChange={set("recipientEmail")} />
        </div>

        <label className="mt-3 block text-sm">
          <span className="mb-1 block font-medium text-encre-700">Message (facultatif)</span>
          <textarea
            value={form.message}
            onChange={set("message")}
            maxLength={500}
            rows={2}
            className="w-full rounded-lg border border-creme-300 px-3 py-2"
          />
        </label>

        {hasEmail && (
          <label className="mt-3 flex items-center gap-2 text-sm text-encre-700">
            <input type="checkbox" checked={sendEmails} onChange={(event) => setSendEmails(event.target.checked)} />
            Envoyer la carte par e-mail
          </label>
        )}

        {sell.isError && (
          <p role="alert" className="mt-3 text-sm font-medium text-grenat-700">
            {sell.error instanceof Error ? sell.error.message : "La vente n'a pas abouti."}
          </p>
        )}

        <Button
          type="submit"
          block
          size="lg"
          className="mt-5"
          loading={sell.isPending}
          disabled={!product || !cents || cents <= 0 || !form.purchaserName.trim()}
        >
          Encaisser {cents ? formatPrice(cents) : ""}
        </Button>
      </form>
    </div>
  );
}

function TextField({
  label,
  ...props
}: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-encre-700">{label}</span>
      <input {...props} className="h-10 w-full rounded-lg border border-creme-300 px-3" />
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/*  Types de cartes                                                            */
/* -------------------------------------------------------------------------- */

const ACCENTS = ["#d96682", "#9a3750", "#c08a5b", "#6f8f72", "#4f6d8f", "#1a1517"];

type ProductDraft = {
  name: string;
  description: string;
  presets: string;
  allowCustomAmount: boolean;
  min: string;
  max: string;
  validityMonths: number;
  accentColor: string;
  sortOrder: number;
  isActive: boolean;
};

const NEW_PRODUCT: ProductDraft = {
  name: "",
  description: "",
  presets: "30, 50, 80",
  allowCustomAmount: true,
  min: "20",
  max: "500",
  validityMonths: 12,
  accentColor: ACCENTS[0]!,
  sortOrder: 0,
  isActive: true,
};

function ProductsPanel({ isManager }: { isManager: boolean }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | "new" | null>(null);

  const products = useQuery({
    queryKey: ["gift-cards", "products"],
    queryFn: () => api.giftCards.products.query({ salonId: SALON_ID }),
  });

  const save = useMutation({
    mutationFn: (input: Parameters<typeof api.giftCards.saveProduct.mutate>[0]) =>
      api.giftCards.saveProduct.mutate(input),
    onSuccess: () => {
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ["gift-cards", "products"] });
    },
  });

  const submit = (draft: ProductDraft, id?: string) =>
    save.mutate({
      id,
      salonId: SALON_ID,
      name: draft.name.trim(),
      description: draft.description.trim() || null,
      presetAmountsCents: draft.presets
        .split(/[;,\s]+/)
        .map((value) => parseEuros(value))
        .filter((value): value is number => value !== null && value > 0),
      allowCustomAmount: draft.allowCustomAmount,
      minAmountCents: parseEuros(draft.min) ?? 2000,
      maxAmountCents: parseEuros(draft.max) ?? 50000,
      validityMonths: draft.validityMonths,
      accentColor: draft.accentColor,
      sortOrder: draft.sortOrder,
      isActive: draft.isActive,
    });

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl">Types de cartes</h2>
          <p className="max-w-2xl text-sm text-encre-500">
            Ce que la vitrine propose à la vente. Modifier un type ne change jamais une carte déjà offerte :
            montant et validité sont figés à l'achat.
          </p>
        </div>
        {isManager && (
          <Button size="sm" onClick={() => setEditing("new")} disabled={editing === "new"}>
            + Type de carte
          </Button>
        )}
      </div>

      {editing === "new" && (
        <div className="mb-3 rounded-xl bg-white p-4 shadow-soft">
          <ProductForm
            initial={NEW_PRODUCT}
            pending={save.isPending}
            error={save.error}
            onCancel={() => setEditing(null)}
            onSave={(draft) => submit(draft)}
          />
        </div>
      )}

      <ul className="grid gap-3 lg:grid-cols-2">
        {products.data?.map((product) => (
          <li key={product.id} className="rounded-xl bg-white p-4 shadow-soft">
            {editing === product.id ? (
              <ProductForm
                initial={{
                  name: product.name,
                  description: product.description ?? "",
                  presets: product.presetAmountsCents.map((c) => String(c / 100)).join(", "),
                  allowCustomAmount: product.allowCustomAmount,
                  min: String(product.minAmountCents / 100),
                  max: String(product.maxAmountCents / 100),
                  validityMonths: product.validityMonths,
                  accentColor: product.accentColor,
                  sortOrder: product.sortOrder,
                  isActive: product.isActive,
                }}
                pending={save.isPending}
                error={save.error}
                onCancel={() => setEditing(null)}
                onSave={(draft) => submit(draft, product.id)}
              />
            ) : (
              <div className="flex gap-4">
                <div
                  aria-hidden
                  className="flex h-20 w-28 shrink-0 flex-col justify-between rounded-lg p-2.5 text-white"
                  style={{ backgroundColor: product.accentColor }}
                >
                  <span className="text-[9px] font-semibold uppercase tracking-[0.16em]">Carte cadeau</span>
                  <span className="font-display text-lg">
                    {product.presetAmountsCents[0] ? formatPrice(product.presetAmountsCents[0]) : "€"}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-lg leading-tight">
                    {product.name}
                    {!product.isActive && (
                      <Badge tone="muted" className="ml-2">
                        Hors vente
                      </Badge>
                    )}
                  </p>
                  {product.description && <p className="text-sm text-encre-500">{product.description}</p>}
                  <p className="mt-1 text-sm text-encre-600">
                    {product.presetAmountsCents.map((c) => formatPrice(c)).join(" · ") || "Aucun montant proposé"}
                    {product.allowCustomAmount &&
                      ` · libre de ${formatPrice(product.minAmountCents)} à ${formatPrice(product.maxAmountCents)}`}
                  </p>
                  <p className="text-xs text-encre-400">
                    Valable {product.validityMonths} mois · {product.soldCount} vendue{product.soldCount > 1 ? "s" : ""}
                  </p>
                </div>
                {isManager && (
                  <Button variant="secondary" size="sm" onClick={() => setEditing(product.id)}>
                    Modifier
                  </Button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ProductForm({
  initial,
  pending,
  error,
  onSave,
  onCancel,
}: {
  initial: ProductDraft;
  pending: boolean;
  error: unknown;
  onSave: (draft: ProductDraft) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const set = <K extends keyof ProductDraft>(key: K, value: ProductDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  return (
    <form
      className="grid gap-3 sm:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(draft);
      }}
    >
      <TextField label="Nom" value={draft.name} onChange={(e) => set("name", e.target.value)} required maxLength={80} />
      <label className="block text-sm">
        <span className="mb-1 block font-medium text-encre-700">Validité</span>
        <select
          value={draft.validityMonths}
          onChange={(event) => set("validityMonths", Number(event.target.value))}
          className="h-10 w-full rounded-lg border border-creme-300 px-3"
        >
          {[3, 6, 12, 18, 24].map((months) => (
            <option key={months} value={months}>
              {months} mois
            </option>
          ))}
        </select>
      </label>
      <div className="sm:col-span-2">
        <TextField
          label="Description (facultatif)"
          value={draft.description}
          onChange={(e) => set("description", e.target.value)}
          maxLength={300}
        />
      </div>
      <div className="sm:col-span-2">
        <TextField
          label="Montants proposés (en euros, séparés par des virgules)"
          value={draft.presets}
          onChange={(e) => set("presets", e.target.value)}
          placeholder="30, 50, 80, 100"
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-encre-700 sm:col-span-2">
        <input
          type="checkbox"
          checked={draft.allowCustomAmount}
          onChange={(event) => set("allowCustomAmount", event.target.checked)}
        />
        Autoriser un montant libre
      </label>
      {draft.allowCustomAmount && (
        <>
          <TextField label="Montant libre minimum (€)" inputMode="decimal" value={draft.min} onChange={(e) => set("min", e.target.value)} />
          <TextField label="Montant libre maximum (€)" inputMode="decimal" value={draft.max} onChange={(e) => set("max", e.target.value)} />
        </>
      )}
      <fieldset className="sm:col-span-2">
        <legend className="mb-1 text-sm font-medium text-encre-700">Couleur de la carte</legend>
        <div className="flex flex-wrap items-center gap-2">
          {ACCENTS.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => set("accentColor", color)}
              aria-label={`Couleur ${color}`}
              aria-pressed={draft.accentColor === color}
              className={[
                "size-8 rounded-full border-2 transition-transform",
                draft.accentColor === color ? "scale-110 border-encre-900" : "border-white",
              ].join(" ")}
              style={{ backgroundColor: color }}
            />
          ))}
          <input
            type="color"
            value={draft.accentColor}
            onChange={(event) => set("accentColor", event.target.value)}
            aria-label="Autre couleur"
            className="h-8 w-10 cursor-pointer rounded border border-creme-300"
          />
        </div>
      </fieldset>
      <label className="flex items-center gap-2 text-sm text-encre-700">
        <input type="checkbox" checked={draft.isActive} onChange={(event) => set("isActive", event.target.checked)} />
        En vente sur le site
      </label>

      {error instanceof Error && (
        <p role="alert" className="text-sm text-grenat-700 sm:col-span-2">
          {error.message}
        </p>
      )}

      <div className="flex gap-2 sm:col-span-2">
        <Button type="submit" loading={pending}>
          Enregistrer
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Annuler
        </Button>
      </div>
    </form>
  );
}
